import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ROTULO_CENTRO_DE_CUSTO,
  ROTULO_FORMA_PAGAMENTO,
  ROTULO_TIPO_LANCAMENTO,
  type DetalheDoLancamento,
} from '@arena/shared/schemas';
import { dinheiro } from '../agenda/formatos.js';
import { dataCompleta, instanteCompleto } from '../alunos/formatos.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { financeiroApi } from '../api/financeiro.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Mini, Status, Tabela, Tag } from '../ui/primitivos.jsx';
import { AcoesDoLancamento, type ModoDaAcao } from './AcoesDoLancamento.jsx';
import { AnexosDoLancamento } from './AnexosDoLancamento.jsx';
import {
  acoesPermitidas,
  corDoLancamento,
  descricaoDaRecorrencia,
  estaEncerrado,
  instrucaoDaOrigem,
  rotuloDaOrigem,
  situacaoDoLancamento,
  valorDoLancamento,
} from './formatos.js';
import { ModalDeLancamento } from './ModalDeLancamento.jsx';

/**
 * O lançamento aberto — o detalhe de uma linha do extrato, em `?lancamento=<id>`.
 *
 * É a ficha DA LINHA e o lugar das ações sobre ela: realizar a previsão,
 * corrigir o manual, estornar o realizado, cancelar a previsão. Quais
 * existem em cada estado é de `acoesDoLancamento` (formatos.ts); o que o
 * perfil alcança é `acoesPermitidas`; nenhum dos dois protege nada — o
 * servidor decide, e o que ele recusa aparece no painel da ação.
 *
 * O LANÇAMENTO AUTOMÁTICO — o que nasceu de fatura, comanda, entrada de
 * mercadoria, reserva ou fechamento de caixa — não tem ação aqui, e a
 * ficha diz ONDE se corrige ("para corrigir, estorne a comanda no Bar"):
 * editá-lo quebraria a conciliação em silêncio, e a correção é desfazer o
 * fato que o gerou. A exceção é a compra a prazo (entrada de mercadoria
 * ainda prevista), cujo pagamento é "Realizar".
 *
 * A URL guarda só o id, e o modal o busca de novo: o link colado abre a
 * mesma ficha, e o que a pessoa vê é sempre o que o servidor tem agora —
 * não a linha que o extrato carregou há uma hora.
 */
export function ModalDoLancamento({
  id,
  hoje,
  fuso,
  onFechar,
  onAbrir,
}: {
  id: string;
  /** Hoje no fuso da arena (`resumo.hoje`): o realizado não passa dele. */
  hoje: string;
  /** O fuso da arena (`resumo.fuso`): os instantes da ficha se escrevem nele. */
  fuso: string;
  onFechar: () => void;
  /** Abre OUTRA linha — o original de um estorno, o estorno de um original. */
  onAbrir: (id: string) => void;
}) {
  const consulta = useQuery({
    // O prefixo ['financeiro'] é o que as ações invalidam: resumo, extrato,
    // categorias e este detalhe refazem juntos.
    queryKey: ['financeiro', 'lancamento', id],
    queryFn: () => financeiroApi.detalhe(id),
    retry: false,
  });

  if (consulta.isPending) {
    return (
      <Modal aberto onFechar={onFechar} titulo="Lançamento">
        <CorpoDeModal>
          <EsqueletoDeCard linhas={5} />
        </CorpoDeModal>
      </Modal>
    );
  }
  if (consulta.isError) {
    const erro = consulta.error;
    return (
      <Modal aberto onFechar={onFechar} titulo="Lançamento">
        <CorpoDeModal>
          <Erro
            tipo={
              erro instanceof ErroDeRede
                ? 'rede'
                : erro instanceof ErroDeApi && erro.ehPermissao
                  ? 'permissao'
                  : 'falha'
            }
            detalhe={mensagemDoErro(erro)}
            onTentarDeNovo={() => void consulta.refetch()}
          />
        </CorpoDeModal>
      </Modal>
    );
  }

  // `key`: abrir outra linha (o estorno, o original) recomeça a ficha — o
  // painel e o que foi digitado nele eram da linha anterior.
  return (
    <FichaDoLancamento
      key={consulta.data.id}
      lancamento={consulta.data}
      hoje={hoje}
      fuso={fuso}
      onFechar={onFechar}
      onAbrir={onAbrir}
    />
  );
}

function FichaDoLancamento({
  lancamento: l,
  hoje,
  fuso,
  onFechar,
  onAbrir,
}: {
  lancamento: DetalheDoLancamento;
  hoje: string;
  fuso: string;
  onFechar: () => void;
  onAbrir: (id: string) => void;
}) {
  const { pode } = useSessao();
  const [modo, setModo] = useState<ModoDaAcao>('ficha');

  // COSMÉTICO: esconde botão. Quem decide é o servidor.
  const acoes = acoesPermitidas(l, pode);

  // Editar reabre o formulário do lançamento, preenchido; fechar (ou salvar)
  // volta para esta ficha. Um modal por vez: o da ficha sai do ar enquanto
  // o da edição está aberto.
  if (modo === 'editar') {
    return <ModalDeLancamento hoje={hoje} lancamento={l} onFechar={() => setModo('ficha')} />;
  }

  const situacao = situacaoDoLancamento(l);
  const encerrado = estaEncerrado(l);
  const original = l.estornoDe;
  const estorno = l.estornadoPor;

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo={l.descricao}
      descricao={`${ROTULO_TIPO_LANCAMENTO[l.tipo]} · ${l.categoria}`}
      rodape={
        <Botao variante="fantasma" onClick={onFechar}>
          Fechar
        </Botao>
      }
    >
      <CorpoDeModal>
        <div>
          <Status situacao={situacao.situacao}>{situacao.rotulo}</Status>
        </div>

        <Mini
          itens={[
            {
              valor: <span style={{ color: corDoLancamento(l) }}>{valorDoLancamento(l)}</span>,
              rotulo: ROTULO_TIPO_LANCAMENTO[l.tipo].toLowerCase(),
            },
            {
              valor: dataCompleta(l.data),
              rotulo: l.situacao === 'Previsto' ? 'data prevista' : 'realizado em',
            },
            {
              valor: l.formaPagamento ? ROTULO_FORMA_PAGAMENTO[l.formaPagamento] : '—',
              rotulo: 'forma de pagamento',
            },
          ]}
        />

        <dl className="detalhes">
          <dt>Categoria</dt>
          <dd>{l.categoria}</dd>
          {l.subcategoria && (
            <>
              <dt>Subcategoria</dt>
              <dd>{l.subcategoria}</dd>
            </>
          )}
          <dt>Centro de custo</dt>
          <dd>
            <Tag>{ROTULO_CENTRO_DE_CUSTO[l.centroDeCusto]}</Tag>
          </dd>
          {l.recorrencia && (
            <>
              <dt>Recorrência</dt>
              <dd>{descricaoDaRecorrencia(l.recorrencia)}</dd>
            </>
          )}
          {l.origemTipo && (
            <>
              <dt>Origem</dt>
              <dd>{rotuloDaOrigem(l.origemTipo)}</dd>
            </>
          )}
        </dl>

        {/* De onde veio o automático e onde se corrige. Só para a linha viva:
            a que já foi estornada ou cancelada conta o desfecho, logo abaixo. */}
        {l.origemTipo && !encerrado && (
          <Aviso>
            <b>Lançamento automático.</b> {instrucaoDaOrigem(l.origemTipo)}.
            {acoes.includes('realizar') &&
              ' O pagamento desta compra a prazo se registra aqui, em “Realizar”.'}
          </Aviso>
        )}
        {original && (
          <Aviso>
            <b>Estorno.</b> Este lançamento desfaz outro
            {l.motivoEstorno ? ` — motivo: ${l.motivoEstorno}` : ''}.{' '}
            <button type="button" className="link" onClick={() => onAbrir(original)}>
              Ver o lançamento original
            </button>
          </Aviso>
        )}
        {estorno && (
          <Aviso>
            <b>Estornado</b> em {dataCompleta(estorno.data)}; o motivo está no estorno.{' '}
            <button type="button" className="link" onClick={() => onAbrir(estorno.id)}>
              Ver o estorno
            </button>
          </Aviso>
        )}
        {l.canceladoEm && (
          <Aviso>
            <b>Previsão cancelada</b> em {instanteCompleto(l.canceladoEm, fuso)}
            {l.motivoCancelamento ? ` — motivo: ${l.motivoCancelamento}` : ''}.
          </Aviso>
        )}

        <AcoesDoLancamento
          lancamento={l}
          acoes={acoes}
          hoje={hoje}
          modo={modo}
          onEscolher={setModo}
          onVoltar={() => setModo('ficha')}
        />

        {l.folha && l.folha.length > 0 && (
          <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Detalhamento da folha</div>
            <Tabela colunas={['Professor', 'Valor']}>
              {l.folha.map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.professor}
                    {item.observacao && (
                      <small style={{ color: 'var(--dim)' }}> · {item.observacao}</small>
                    )}
                  </td>
                  <td className="num">{dinheiro(item.valor)}</td>
                </tr>
              ))}
            </Tabela>
          </section>
        )}

        <AnexosDoLancamento
          lancamentoId={l.id}
          anexos={l.anexos}
          podeEditar={pode('financeiro', 'editar')}
        />
      </CorpoDeModal>
    </Modal>
  );
}

/** Informação que a pessoa precisa para agir — e, por isso, não some no modo apresentação como a `Nota`. */
function Aviso({ children }: { children: React.ReactNode }) {
  return (
    <div className="aviso">
      <Icone nome="info" />
      <div>{children}</div>
    </div>
  );
}
